"use client";

import React, { useCallback, useEffect, useRef, useState } from "react";
import { useRouter } from "next/navigation";
import { usePayPalSDK } from "@/hooks/use-paypal-sdk";
import {
  APPLE_PAY_ENABLED,
  GOOGLE_PAY_ENABLED,
  PAYPAL_NEGATIVE_TESTING_CLIENT,
  IS_PAYPAL_SANDBOX,
  E2E_PAYPAL_MOCK_ENABLED,
} from "@/lib/config";
import { getCountryName } from "@/lib/paypal-countries";
import { AlertTriangle, Loader2, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { DEFAULT_CURRENCY, isZeroDecimalCurrency } from "@/lib/currencies";
import {
  isShippingAddressComplete,
  isShippingFormComplete,
} from "./checkout-form";
import ApplePayButton from "./apple-pay-button";
import GooglePayButton from "./google-pay-button";
import { StripeEmbeddedCheckoutInline } from "@/components/checkout/stripe-embedded-checkout-inline";
import type {
  CheckoutIntent,
  CheckoutIntentDraft,
} from "@/lib/payments/checkout-intent";
import {
  capturePayPalOrder,
  createPayPalOrder,
  createStripeCheckoutSession,
} from "@/lib/payments/checkout-actions";
import {
  PAYMENTS_ERROR_CODES,
  PaymentCreateError,
} from "@/lib/payments/error-codes";
import {
  parseApiResponse,
  SellerProviderResponseSchema,
  isStateRequiredForCountry,
  type CheckoutPayload,
  type ShippingRatesParcel,
} from "@kraahl/contract";
import type { CheckoutPayloadExternalShipping } from "@/lib/checkout-payload";
import { LiveRateSubflow } from "@/components/cart/live-rate-checkout-subflow";
import type { ShippingRateSelection } from "@/hooks/use-shipping-rates";
import type { ShippingRateIntent } from "@/lib/checkout/build-shipping-rates-request";
import type { ShippingMode } from "@/lib/checkout/derive-shipping-mode";
import { useOverlay } from "@/components/context/overlay-context";

export type { SellerDetail } from "@/lib/payments/seller-detail";
import type { SellerDetail } from "@/lib/payments/seller-detail";

import type { ShippingFormData } from "./shipping-form";

interface PayPalCheckoutProps {
  /**
   * Draft checkout intent — the caller assembles it from cart state + shipping
   * form. For `flat_rate` the draft equals the wire shape. For `live_rate` the
   * draft may carry no `externalShipping` yet — this component owns the
   * sub-flow FSM that locks the carrier rate and folds the lock into
   * {@link effectiveIntent} before any POST. See
   * {@link CheckoutIntentLiveRateDraft} for the type rationale.
   */
  intent: CheckoutIntentDraft | null;
  /**
   * Order total in the buyer's currency, used for Apple/Google Pay button
   * display. Server is still the money authority on capture; this number
   * only paints the wallet UI before approval.
   */
  totalAmount: number;
  sellerDetails: SellerDetail[];
  currencyCode?: string;
  formData: ShippingFormData | null;
  selectedCountry?: string;
  onApproveAction: (paypalData: {
    orderID: string;
    captureID?: string;
    captureIds?: Record<string, string>;
    orderNumber?: string;
    orderId?: string;
    auctionSettled?: boolean;
    offerSettled?: boolean;
  }) => void;
  /**
   * Build the capture-route payload at click time. The optional `extras`
   * carries the live-rate selection (FSM-local to this component, the parent
   * cart doesn't know about it) — see `buildCheckoutPayload` in
   * `lib/checkout-payload.ts`. Threading this prevents the "parallel-channel
   * data loss" bug where intent has externalShipping but the capture-side
   * payload did not — see
   * `.tondocs/shippo/HANDOFF_EXTERNAL_SHIPPING_CAPTURE_GAP_2026_06_26.md`.
   *
   * MISU note: when `extras.externalShipping` is present the builder returns
   * `CheckoutPayload & { kind: "live_rate" }`; when absent, `kind: "flat_rate"`.
   * The closure callsites pass `extras?.externalShipping ?? null` and let the
   * builder decide — no callsite "chooses" the kind explicitly.
   */
  getCheckoutPayload?: (extras?: {
    externalShipping?: CheckoutPayloadExternalShipping | null;
  }) => CheckoutPayload | null;
  disabled?: boolean;
  isCountryBannedForShipping?: (countryCode: string) => boolean;
  /**
   * WS4.3c — Shippo live-rate routing. When `'live_rate'` AND `parcel` is
   * non-null AND a `formData` address is available, the component renders
   * `LiveRateSubflow` (rate-picker only) in place of the PayPal button area
   * until the buyer confirms a rate. Anything else (including `undefined`)
   * is the legacy flat-rate path — byte-for-byte unchanged.
   *
   * @see .tondocs/shippo/PLAN.md §D WS4 (per-seller routing)
   */
  shippingMode?: ShippingMode;
  /** Aggregated parcel for the active seller's items. See `aggregateParcel`. */
  parcel?: ShippingRatesParcel;
  /**
   * Preview hook for the live-rate amount. Fires with the buyer's currently
   * highlighted rate (BEFORE Continue) so the caller can refresh the
   * displayed total inline. Fires with the confirmed rate on Continue, and
   * with `null` when the subflow is cancelled / unmounted. No-op for
   * legacy flat-rate sellers.
   */
  onLiveRateAmountChange?: (amount: number | null) => void;
}

/**
 * Outer FSM for the WS4.3c routing decision. Discriminated union ensures the
 * compiler enforces "ready" carries a selection; "legacy" carries nothing.
 *
 *  - `legacy`               — flat-rate seller; existing PayPal button area renders.
 *  - `live_rate_collecting` — sub-flow mounted; PayPal hidden until onComplete.
 *  - `live_rate_ready`      — sub-flow done; PayPal renders with externalShipping folded in.
 *
 * @see .tondocs/skills/react-state-design.md §4 (FSM discriminated union)
 * @see .tondocs/skills/type-driven-design.md §1 (ADT, illegal-states unrepresentable)
 */
type CheckoutMode =
  | { kind: "legacy" }
  | { kind: "live_rate_collecting" }
  | {
      kind: "live_rate_ready";
      selection: ShippingRateSelection;
    };

function unreachable(x: never): never {
  throw new Error(`paypal-checkout: unreachable mode: ${JSON.stringify(x)}`);
}

/**
 * Section label above a payment rail. Presentational only — the rails
 * themselves stay two independent JSX blocks (they share no state), so this is
 * a heading, not a wrapper: wrapping them in a card would imply a grouping the
 * layout doesn't have.
 */
function RailHeading({ children }: { children: React.ReactNode }) {
  return (
    <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
      {children}
    </div>
  );
}

/**
 * Settle time before the Stripe rail mints a Checkout Session for a changed
 * payload. Without the CTA the mint is driven by the payload itself, and the
 * payload changes on every committed edit upstream (address field blur,
 * quantity step, shipping-method pick) — each of which would otherwise be its
 * own POST. Sized to swallow a burst of edits while staying imperceptible once
 * the buyer stops: the route rate-limits at 30/min per user (burst 10), which
 * this keeps a long way off.
 */
const STRIPE_MINT_DEBOUNCE_MS = 350;

/**
 * Map the upstream `ShippingForm`'s shape onto the subflow's `address` prop.
 * Returns `null` when any field the rate request needs is blank — keeps the
 * live-rate gate (`isLiveRateRouting`) closed until the buyer completes the
 * address upstream. ISO/long-form country is normalised inside the builder.
 */
export function formDataToRateAddress(
  fd: ShippingFormData,
): ShippingRateIntent["address"] | null {
  const name = `${fd.firstName ?? ""} ${fd.lastName ?? ""}`.trim();
  const street1 = (fd.address ?? "").trim();
  const city = (fd.city ?? "").trim();
  const state = (fd.state ?? "").trim();
  const zip = (fd.postalCode ?? "").trim();
  const country = (fd.country ?? "").trim();
  // `state` is required only for countries that PayPal/Shippo treat as having
  // provinces (US, CA). Buyers in FR/UK/JP/… legitimately have state="". The
  // SoT `isStateRequiredForCountry` matches the upstream gate
  // (`isShippingAddressComplete`) and the Shippo Zod schema, so this gate
  // can't desync from them. See [[project_state_validation_desync_us_ca_only]].
  if (!name || !street1 || !city || !zip || !country) return null;
  if (isStateRequiredForCountry(country) && !state) return null;
  const street2 = (fd.apartment ?? "").trim();
  // Compose consignee phone as `${countryCode}${number}` (mirror of how
  // `cart.tsx::completeOrder` composes it for PayPal shipping address). Both
  // parts are optional in `ShippingFormData` — send phone only when BOTH are
  // present so we never emit a malformed `"+33"` or bare number. Threads
  // through Zod (`.optional()`), builder, route, `lib/shipping/rates.ts`
  // addressTo, then Shippo `shipments.create`. Missing phone = DHL Express
  // silently dropped from `rates[]` (probe 2026-07-03, US→FR).
  const phoneCountryCode = (fd.phoneCountryCode ?? "").trim();
  const phoneNumber = (fd.phoneNumber ?? "").trim();
  const phone = phoneCountryCode && phoneNumber
    ? `${phoneCountryCode}${phoneNumber}`
    : "";
  const email = (fd.email ?? "").trim();
  return {
    name,
    street1,
    ...(street2 ? { street2 } : {}),
    city,
    state,
    zip,
    country,
    ...(phone ? { phone } : {}),
    ...(email ? { email } : {}),
  };
}

export default function PayPalCheckout({
  intent,
  totalAmount,
  sellerDetails,
  currencyCode = DEFAULT_CURRENCY,
  formData,
  selectedCountry,
  onApproveAction,
  getCheckoutPayload,
  disabled = false,
  isCountryBannedForShipping,
  shippingMode,
  parcel,
  onLiveRateAmountChange,
}: PayPalCheckoutProps) {
  // Outer FSM (WS4.3c). Legacy mode is the byte-for-byte unchanged default —
  // `shippingMode !== 'live_rate'` collapses to `{ kind: 'legacy' }` and every
  // downstream branch reads it as a no-op. The subflow needs a non-null
  // `formData` (address is owned upstream by ShippingForm) — without it the
  // gate stays in `legacy` until the buyer completes the address fields.
  const subflowAddress = formData ? formDataToRateAddress(formData) : null;
  const isLiveRateRouting =
    shippingMode === "live_rate" &&
    parcel != null &&
    sellerDetails[0]?.sellerId !== undefined &&
    subflowAddress !== null;
  const [checkoutMode, setCheckoutMode] = useState<CheckoutMode>(() =>
    isLiveRateRouting ? { kind: "live_rate_collecting" } : { kind: "legacy" },
  );

  // FSM ↔ gate sync. Two transitions to keep in step with the live-rate gate:
  //  1) Gate opens after mount: `useState` initializer ran when `isLiveRateRouting`
  //     was still false (buyer hadn't completed the address yet), so the FSM
  //     stuck in `legacy`. Promote `legacy → live_rate_collecting` once the gate
  //     opens — the subflow then mounts and auto-fetches.
  //  2) Gate closes after mount: a previously-complete address went incomplete
  //     (buyer cleared a field, switched country, etc). Demote back to `legacy`
  //     so the subflow unmounts cleanly.
  // Address-change re-entry: buyer confirmed a rate (FSM = `live_rate_ready`),
  // then edited the address. Demote back to `collecting` so the stale rateId
  // doesn't ride into PayPal create. First mount is skipped — the initial
  // `useState` value covers it.
  const addressKey = subflowAddress ? JSON.stringify(subflowAddress) : null;
  const prevAddressKeyRef = useRef<string | null>(addressKey);
  useEffect(() => {
    const addressChanged = prevAddressKeyRef.current !== addressKey;
    prevAddressKeyRef.current = addressKey;
    setCheckoutMode((prev) => {
      if (isLiveRateRouting && prev.kind === "legacy") {
        return { kind: "live_rate_collecting" };
      }
      if (!isLiveRateRouting && prev.kind !== "legacy") {
        return { kind: "legacy" };
      }
      if (addressChanged && prev.kind === "live_rate_ready") {
        return { kind: "live_rate_collecting" };
      }
      return prev;
    });
  }, [isLiveRateRouting, addressKey]);

  // The buyer-confirmed live-rate selection is folded into the intent before
  // the PayPal builder is called. Kept derived from the FSM (no mirror state).
  const externalShipping =
    checkoutMode.kind === "live_rate_ready"
      ? {
          rateId: checkoutMode.selection.providerRateId,
          shipmentId: checkoutMode.selection.providerShipmentId,
          amount: checkoutMode.selection.amount,
        }
      : null;

  // Narrow the draft to the wire shape:
  //  - flat_rate  → already CheckoutIntent (draft == wire on this branch).
  //  - live_rate  → fold the FSM-local `externalShipping` lock in. Without
  //    the lock the draft has no externalShipping and CANNOT be POSTed —
  //    `effectiveIntent` stays null, isButtonDisabled gates the rails.
  //
  // PR-E Move 2 (Sibling-F · F2): externalShipping is REQUIRED on the
  // `live_rate` wire variant — illegal state structurally unrepresentable.
  // The draft type (2026-06-30 fix) lets the cart-layer builder emit a
  // partial live_rate intent before the FSM has locked a rate; this site
  // is the single seam that converts draft → wire.
  const effectiveIntent: CheckoutIntent | null = !intent
    ? null
    : intent.kind === "flat_rate"
      ? intent
      : externalShipping
        ? { ...intent, externalShipping }
        : null;

  const handleSubflowComplete = useCallback(
    (selection: ShippingRateSelection) => {
      // Race-safety: if the FSM has moved on (cancel, parent re-mount), drop
      // the call. The sub-flow guards single-fire on its end, but the outer
      // guard ensures concurrency-design §7 ack-once still holds if the parent
      // re-renders with a stale `onComplete` identity.
      setCheckoutMode((prev) =>
        prev.kind === "live_rate_collecting"
          ? { kind: "live_rate_ready", selection }
          : prev,
      );
    },
    [],
  );

  const handleSubflowCancel = useCallback(() => {
    setCheckoutMode((prev) =>
      prev.kind === "live_rate_collecting" ? { kind: "legacy" } : prev,
    );
    onLiveRateAmountChange?.(null);
  }, [onLiveRateAmountChange]);

  // Forward selection-preview amount to parent so the displayed total can
  // refresh in real time while the buyer scans rates. Latest-ref to avoid
  // re-firing on parent callback identity churn.
  const onLiveRateAmountChangeRef = useRef(onLiveRateAmountChange);
  useEffect(() => {
    onLiveRateAmountChangeRef.current = onLiveRateAmountChange;
  }, [onLiveRateAmountChange]);
  const handleSubflowSelectionChange = useCallback(
    (selection: ShippingRateSelection | null) => {
      onLiveRateAmountChangeRef.current?.(selection?.amount ?? null);
    },
    [],
  );

  // FSM-driven reset: leaving live-rate mode (e.g. address went incomplete →
  // `legacy`) clears the preview amount. The `live_rate_ready` confirmed
  // amount is preserved because we re-affirm via the FSM in that branch.
  useEffect(() => {
    if (checkoutMode.kind === "legacy") {
      onLiveRateAmountChangeRef.current?.(null);
    } else if (checkoutMode.kind === "live_rate_ready") {
      onLiveRateAmountChangeRef.current?.(checkoutMode.selection.amount);
    }
  }, [checkoutMode]);

  const [railResolved, setRailResolved] = useState(false);
  // Buyer chooses between rails when both are available. Order is significant:
  // the API returns Stripe first when both apply, so the UI renders Stripe on
  // top and PayPal below. When only one rail applies the array has one entry;
  // an empty array means the seller is not currently accepting payments.
  const [availableProviders, setAvailableProviders] = useState<("paypal" | "stripe")[]>(["paypal"]);
  const router = useRouter();
  const { closeUtility } = useOverlay();
  // Stripe rail FSM (react-state-design §4 — mutually exclusive phases as a
  // discriminated union, never parallel booleans). Replaces the pre-2026-07-29
  // `{ open } + stripeBusy` pair, which only had to model "did the buyer click
  // the CTA": the CTA is gone and the Session is minted automatically as soon
  // as the checkout payload is complete, so minting/error/already-paid are now
  // states the buyer can land in without ever having acted.
  //
  //   idle         — rail not applicable, or payload not complete enough to mint
  //   minting      — POST /api/stripe/checkout-session in flight (debounced)
  //   ready        — Session minted; `<StripeEmbeddedCheckoutInline>` mounts
  //   already_paid — this exact payload was already paid (unconsumed draft);
  //                  we offer a link to the return page rather than yanking the
  //                  buyer there — auto-navigating on mount would bounce anyone
  //                  who reopens the cart in the payment→webhook window.
  //   error        — mint failed; buyer gets an explicit retry (no silent rail)
  const [stripeRail, setStripeRail] = useState<
    | { kind: "idle" }
    | { kind: "minting" }
    | {
        kind: "ready";
        clientSecret: string;
        sessionId: string;
        sellerStripeAccountId: string;
      }
    | { kind: "already_paid"; sessionId: string }
    | { kind: "error"; message: string }
  >({ kind: "idle" });
  // Bumped by the error-state Retry button; re-runs the mint effect without
  // needing the payload (and therefore its cache key) to change.
  const [stripeMintAttempt, setStripeMintAttempt] = useState(0);

  const showStripe = availableProviders.includes("stripe");
  const showPayPal = availableProviders.includes("paypal");
  // Both rails render simultaneously now (2026-07-29), so each needs a label
  // saying whose payment methods those are. With a single rail the heading
  // would just name the only thing on screen — skip it and keep the slot as
  // uncluttered as it was before this change.
  const bothRailsOffered = showStripe && showPayPal;

  useEffect(() => {
    const sid = sellerDetails[0]?.sellerId;
    if (!sid) {
      setRailResolved(true);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(
          `/api/payments/seller-provider?sellerId=${encodeURIComponent(sid)}`,
        );
        if (!res.ok) {
          if (!cancelled) setAvailableProviders(["paypal"]);
          return;
        }
        // Boundary parse — contract pins `{ provider, providers, chosen }`.
        // The previous 4-layer fallback (providers ?? chosen ?? provider) is
        // gone: SellerProviderResponseSchema makes `providers` canonical.
        const data = await parseApiResponse(res, SellerProviderResponseSchema);
        if (cancelled) return;
        setAvailableProviders(data.providers);
      } catch {
        if (!cancelled) setAvailableProviders(["paypal"]);
      } finally {
        if (!cancelled) setRailResolved(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [sellerDetails]);

  const partnerMerchantId = process.env.NEXT_PUBLIC_PAYPAL_PARTNER_ID;

  if (railResolved && showPayPal && !partnerMerchantId) {
    throw new Error("PayPal configuration error: NEXT_PUBLIC_PAYPAL_PARTNER_ID missing");
  }

  // Déterminer le pays à vérifier (celui du formulaire complet ou celui sélectionné)
  const countryToCheck = formData?.country || selectedCountry;
  const isCountryBanned = countryToCheck && isCountryBannedForShipping
    ? isCountryBannedForShipping(countryToCheck)
    : false;

  // Single rail gate: `effectiveIntent` is null in two cases that BOTH must
  // disable PayPal + Stripe:
  //  1) Builder couldn't assemble an intent at all (no items / missing context
  //     / missing flat-rate shippingMethodId / etc).
  //  2) live_rate draft is in flight and the buyer has not locked a rate yet
  //     (sub-flow gate). The wire shape requires externalShipping; until the
  //     FSM hits `live_rate_ready` we don't have it, so the draft → wire
  //     conversion returns null.
  // Pre-fix (2026-06-30) this gated on `!intent || liveRateMissingSelection`
  // — `intent` was the wire shape so the gate doubled as the assembly check.
  // With the draft type the wire shape lives in `effectiveIntent`, so it is
  // the single source of truth.
  const isButtonDisabled =
    disabled || !formData || isCountryBanned || !effectiveIntent;

  // Buyer-facing reason for a dead button. The country-banned case has its
  // own dedicated alert above (with a country name in the copy) so we suppress
  // this banner there — duplicating two amber rectangles looks like a bug.
  // Priority order matches the gate order in `isButtonDisabled`: surface the
  // earliest reason the buyer can act on, not a downstream symptom (an
  // incomplete address makes `effectiveIntent` null too, but "Add a shipping
  // address" is more useful than "Checkout is not ready").
  //
  // Pre-2026-06-30 the button silently stayed disabled with no hint — the
  // F2 refactor exposed an existing UX gap (no signal for the incomplete-
  // address path either); fixing it here covers all gates uniformly.
  const disabledReason: string | null = (() => {
    if (!isButtonDisabled) return null;
    if (isCountryBanned) return null; // dedicated alert renders above
    if (!isShippingAddressComplete(formData)) {
      return "Add a complete shipping address (name, street, city, state where required, postal code, country) to continue.";
    }
    if (!isShippingFormComplete(formData)) {
      return "Add email and phone number to continue.";
    }
    if (shippingMode === "live_rate" && !parcel) {
      return "This seller hasn't published parcel dimensions yet — checkout can't quote a carrier rate. Message the seller or come back later.";
    }
    if (!effectiveIntent) {
      // live_rate sub-flow renders LiveRateSubflow instead of this banner —
      // the early `switch` above returns before we get here, so this branch
      // is reachable only for flat_rate intents that couldn't assemble.
      return "Select a shipping method on each item to continue.";
    }
    if (disabled) {
      // Parent-level disable that we don't have detail for. Generic but
      // honest — at least tells the buyer the cart, not the button, is the
      // thing to revisit.
      return "Some items in this checkout aren't ready (out of stock, shipping not picked, or seller paused). Review the cart and try again.";
    }
    return null;
  })();

  // Per-seller digital wallet capability check
  const allSellersHaveApplePay = sellerDetails.every(s => s.capabilities?.includes("APPLE_PAY"));
  const allSellersHaveGooglePay = sellerDetails.every(s => s.capabilities?.includes("GOOGLE_PAY"));

  // v6 SDK lifecycle
  const { sdk, isLoading, isPayPalEligible, isApplePayEligible, isGooglePayEligible, error: sdkError } = usePayPalSDK({
    currencyCode,
    enabled: railResolved && showPayPal && !isButtonDisabled,
  });

  // Payment error state — shown as inline banner above buttons
  const [paymentError, setPaymentError] = useState<{ type: string; message: string } | null>(null);
  const retryCountRef = useRef(0);
  const isPaymentInProgressRef = useRef(false);
  const MAX_RETRIES = 3;

  // Negative testing (sandbox + demo mode only)
  const showDemoPanel = PAYPAL_NEGATIVE_TESTING_CLIENT && IS_PAYPAL_SANDBOX;
  const [demoMockCode, setDemoMockCode] = useState<string>("");

  const zeroDecimal = isZeroDecimalCurrency(currencyCode);
  const totalAmountStr = zeroDecimal
    ? String(Math.round(totalAmount))
    : Number.isFinite(totalAmount)
      ? totalAmount.toFixed(2)
      : "0.00";

  // Refs to avoid stale closures in the click handler. `effectiveIntent`
  // folds the live-rate `externalShipping` selection in when the outer FSM is
  // `live_rate_ready` — legacy path reads through as `intent` unchanged.
  const intentRef = useRef(effectiveIntent);
  intentRef.current = effectiveIntent;
  const onApproveRef = useRef(onApproveAction);
  onApproveRef.current = onApproveAction;
  const getCheckoutPayloadRef = useRef(getCheckoutPayload);
  getCheckoutPayloadRef.current = getCheckoutPayload;

  const demoMockCodeRef = useRef(demoMockCode);
  demoMockCodeRef.current = demoMockCode;

  // Decline-retry order reuse (CHECKOUT_RETRY.md known-limitation, closed
  // 2026-07-05): PayPal recommends presenting the SAME order id again after
  // INSTRUMENT_DECLINED so the declined funding source is hidden on retry —
  // and reuse means a lost-capture retry maps to the same order (mirror of
  // the mobile cardSession guard). Keyed by intent CONTENT (the error object
  // carries no orderId); invalidated on capture success, on intent change,
  // and on any non-declined error.
  const cachedOrderIdRef = useRef<string | null>(null);
  const cachedIntentHashRef = useRef<string | null>(null);
  const paymentErrorTypeRef = useRef<string | null>(null);
  paymentErrorTypeRef.current = paymentError?.type ?? null;
  useEffect(() => {
    if (cachedIntentHashRef.current === null) return;
    const currentHash = effectiveIntent ? JSON.stringify(effectiveIntent) : null;
    if (cachedIntentHashRef.current !== currentHash) {
      cachedOrderIdRef.current = null;
      cachedIntentHashRef.current = null;
    }
  }, [effectiveIntent]);

  // Shared order creation — returns order ID string.
  // paymentSource is required for Google Pay (SCA verification attributes).
  // fundingSource signals the rail to the server so it picks the right
  // soft_descriptor (Fix 1: "card" → "KRAAHL <seller>", anything else → "KRAAHL").
  //
  // Body assembly + schema parse + fetch all live in `createPayPalOrder`
  // (see `lib/payments/checkout-actions.ts`). This wrapper is the React-side
  // adapter: it pulls the latest intent from the ref and surfaces the same
  // "Create: missing checkout intent" error the inline implementation did,
  // so the component-side error classification keeps working unchanged.
  async function createOrderFromIntent(
    paymentSource?: Record<string, any>,
    fundingSource?: "paypal" | "card",
  ): Promise<string> {
    const currentIntent = intentRef.current;
    if (!currentIntent) {
      throw new Error("Create: missing checkout intent (debug: n/a)");
    }
    const intentHash = JSON.stringify(currentIntent);
    // Reuse ONLY on a declined retry with unchanged checkout content —
    // never across cart/address/rate changes (hash) and never after a
    // non-declined failure (cache cleared in handlePaymentError).
    if (
      cachedOrderIdRef.current &&
      cachedIntentHashRef.current === intentHash &&
      retryCountRef.current > 0 &&
      paymentErrorTypeRef.current === "declined"
    ) {
      console.log(
        "[PayPal] Reusing order id after decline:",
        cachedOrderIdRef.current,
      );
      return cachedOrderIdRef.current;
    }
    const { id } = await createPayPalOrder({
      intent: currentIntent,
      paymentSource,
      fundingSource,
      mockCode: demoMockCodeRef.current || undefined,
    });
    cachedOrderIdRef.current = id;
    cachedIntentHashRef.current = intentHash;
    return id;
  }

  // Shared capture — used by all payment methods (PayPal, Card, Apple Pay, Google Pay)
  async function captureAndNotify(orderId: string): Promise<void> {
    // Collect checkout data for server-side order creation (safer than client-side)
    // Thread the FSM-local live-rate selection into the capture-side payload —
    // the parent cart doesn't know about externalShipping, so we attach it here.
    // [[HANDOFF_EXTERNAL_SHIPPING_CAPTURE_GAP_2026_06_26]] — without this the
    // CAPTURE flow drops external_shipment_id / external_rate_id silently.
    const extrasForPayload =
      externalShipping && intent
        ? {
            externalShipping: {
              sellerId: intent.sellerId,
              shipmentId: externalShipping.shipmentId,
              rateId: externalShipping.rateId,
              amount: externalShipping.amount,
              carrierProvider: "shippo" as const,
            },
          }
        : undefined;
    const checkoutData =
      getCheckoutPayloadRef.current?.(extrasForPayload) || null;

    // `capturePayPalOrder` owns the wire shape (snake-case `order_id`,
    // mockCode-allowlist coupling) and the partial-failure rule (capture
    // succeeded → no throw even on non-2xx, so reconciliation recovers).
    const result = await capturePayPalOrder({
      orderId,
      // Type widening: `CheckoutPayload` is a discriminated union; the
      // `checkoutData` parameter is wire-shape (`Record<string, unknown>`).
      // The double-cast through `unknown` is required by tsc because the
      // union doesn't have an index signature — same wire bytes either way.
      checkoutData: checkoutData
        ? (checkoutData as unknown as Record<string, unknown>)
        : undefined,
      mockCode: demoMockCodeRef.current || undefined,
    });

    const captureData = result.capture as
      | { purchase_units?: { reference_id?: string; payments?: { captures?: { id?: string }[] } }[] }
      | undefined;
    const orderResult = result.orderResult; // Server-side order creation result

    // Build seller → captureId map from all units
    const captureIds: Record<string, string> = {};
    const purchaseUnits = captureData?.purchase_units || [];
    for (const unit of purchaseUnits) {
      const refId = unit.reference_id;
      const capId = unit?.payments?.captures?.[0]?.id;
      if (refId && capId) {
        captureIds[refId] = capId;
      }
    }

    const vendorUnit = captureData?.purchase_units?.[0];
    const captureID = vendorUnit?.payments?.captures?.[0]?.id;

    // Success — reset retry counter and clear any error
    retryCountRef.current = 0;
    setPaymentError(null);
    isPaymentInProgressRef.current = false;
    // The captured order is consumed — the next attempt must mint fresh.
    cachedOrderIdRef.current = null;
    cachedIntentHashRef.current = null;

    onApproveRef.current({
      orderID: orderId,
      captureID,
      captureIds,
      orderNumber: orderResult?.orderNumber,
      orderId: orderResult?.orderId,
      auctionSettled: orderResult?.auctionSettled,
      offerSettled: orderResult?.offerSettled,
    });
  }

  const captureAndNotifyRef = useRef(captureAndNotify);
  captureAndNotifyRef.current = captureAndNotify;

  // Classify PayPal error into user-facing message
  function classifyPaymentError(err: PayPalV6Error): { type: string; message: string } {
    // T1 (HANDOFF_DROP_RESILIENCE_2026_06_26): seller-side restrictions
    // surface as a typed PaymentCreateError thrown by `createPayPalOrder`
    // in lib/payments/checkout-actions.ts. The route classifies PayPal's
    // PAYEE_* / RECEIVING_LIMIT_EXCEEDED / siblings → 409 +
    // SELLER_PAYPAL_RESTRICTED, the builder rethrows as a typed error, and
    // the component switches on `error.code`. Replaces the H1 regex hotfix
    // (`/^Create:\s+([A-Z0-9_]+)/` on err.message) — no string-matching
    // across the throw boundary anymore.
    if (err instanceof PaymentCreateError) {
      if (err.code === PAYMENTS_ERROR_CODES.SELLER_PAYPAL_RESTRICTED) {
        // The route stamps `paypalIssue` so future copy can split between
        // "verification pending" (PAYEE_ACCOUNT_NOT_VERIFIED) and the
        // generic "receiving limit / restriction" copy. For now one banner
        // — operators get the discrimination via the Axiom log line.
        return {
          type: 'seller_paypal',
          message:
            "The seller can't accept payments right now (PayPal account restriction). This isn't an issue with your card or account — please contact the seller or try again later.",
        };
      }
      // Other typed create-failure codes (PAYPAL_CREATE_FAILED, the legacy
      // generic) collapse to the same "try again" branch as before.
      return { type: 'capture', message: 'Payment failed. Please try again.' };
    }

    const code = err?.code || '';

    switch (code) {
      case 'INSTRUMENT_DECLINED':
        return { type: 'declined', message: 'Your payment method was declined. Please try a different payment method.' };
      case 'INSUFFICIENT_FUNDS':
        return { type: 'declined', message: 'Insufficient funds. Please try a different payment method.' };
      case 'CARD_EXPIRED':
        return { type: 'declined', message: 'Your card has expired. Please use a different card.' };
      case 'TRANSACTION_REFUSED':
        return { type: 'refused', message: 'This payment could not be processed. Please try again later or use a different method.' };
      case 'PAYER_CANNOT_PAY':
        return { type: 'refused', message: 'Your PayPal account cannot complete this payment. Please try a different payment method.' };
      case 'INTERNAL_SERVER_ERROR':
        return { type: 'network', message: 'PayPal is temporarily unavailable. Please try again in a moment.' };
      case 'NETWORK_ERROR':
        return { type: 'network', message: 'Connection issue with the payment provider. Please try again.' };
    }

    if (err?.message?.toLowerCase().includes('network')) {
      return { type: 'network', message: 'Connection issue with the payment provider. Please try again.' };
    }
    // Capture-level errors (thrown from captureAndNotify)
    const msg = err?.message || '';
    if (msg.startsWith('Capture:') || msg.startsWith('Create:')) {
      return { type: 'capture', message: 'Payment failed. Please try again.' };
    }
    return { type: 'unknown', message: 'Something went wrong with your payment. Please try again.' };
  }

  // Error handler — ref-guarded to avoid stale closures in session callbacks
  function handlePaymentError(err: PayPalV6Error) {
    console.error('[PayPal] Payment error:', { code: err?.code, message: err?.message });
    isPaymentInProgressRef.current = false;
    retryCountRef.current += 1;

    const classified = classifyPaymentError(err);
    // Order reuse is a decline-only affordance: any other failure class
    // (capture error, seller restriction, unknown) invalidates the cached
    // order so the next attempt starts clean.
    if (classified.type !== 'declined') {
      cachedOrderIdRef.current = null;
      cachedIntentHashRef.current = null;
    }

    if (retryCountRef.current >= MAX_RETRIES) {
      setPaymentError({
        type: 'max_retries',
        message: `${classified.message} If this keeps happening, please contact support.`,
      });
      return;
    }

    setPaymentError(classified);
  }
  const handlePaymentErrorRef = useRef(handlePaymentError);
  handlePaymentErrorRef.current = handlePaymentError;

  // Shared session callbacks — used by PayPal and Card buttons
  const sessionCallbacks: PayPalV6SessionOptions = {
    onApprove: async ({ orderId }: { orderId: string }) => {
      await captureAndNotifyRef.current(orderId);
    },

    onCancel: () => {
      console.log('[PayPal] Payment cancelled by user');
      isPaymentInProgressRef.current = false;
    },

    onError: (err: PayPalV6Error) => {
      handlePaymentErrorRef.current(err);
    },
  };

  // Order creation promise factory for PayPal/Card sessions.
  // `fundingSource` flows through to the server so soft_descriptor is rail-aware.
  function createOrderPromise(fundingSource: "paypal" | "card"): Promise<{ orderId: string }> {
    return (async () => {
      const orderId = await createOrderFromIntent(undefined, fundingSource);
      return { orderId };
    })();
  }

  // Attach click handlers to the PayPal web components.
  //
  // React 19 callback ref (returns a cleanup) instead of `useRef` + a
  // separate `useEffect(..., [sdk, ...])`. The old shape leaked when the
  // Stripe inline checkout mounted/unmounted next to us:
  //   - `<paypal-button>` sits inside `{!stripeCheckoutState.open && (...)}`.
  //     Opening Stripe unmounts it → `buttonRef.current` becomes null but
  //     the `useEffect` deps `[sdk, isLoading, isPayPalEligible]` don't
  //     change, so its cleanup never runs.
  //   - Cancelling Stripe re-mounts a FRESH `<paypal-button>` → ref points
  //     at the new DOM node, but the effect STILL doesn't re-run → the
  //     new button has no click handler and looks dead.
  // Callback refs fire on every attach/detach, so the handler follows the
  // element regardless of what the payment-rail-selector state is doing.
  const attachPayPalButton = useCallback(
    (el: HTMLElement | null) => {
      if (!el || !sdk) return;
      const handleClick = async () => {
        if (isPaymentInProgressRef.current) return;
        isPaymentInProgressRef.current = true;
        setPaymentError(null);
        try {
          if (E2E_PAYPAL_MOCK_ENABLED) {
            // E2E test-mode hook — .tondocs/infra/PAYPAL_CAPTCHA_BLOCKER.md
            // §5 variant A. `session.start()` is what opens the sandbox
            // buyer-login popup reCAPTCHA Enterprise blocks in CI; skip it
            // and go straight to `onApprove` with the orderId from a REAL
            // create-order call (server-to-server sandbox REST, no popup,
            // not captcha-gated). The capture leg this triggers is mocked at
            // the network layer by the Playwright spec — see
            // tests/e2e/paypal-checkout-smoke.spec.ts for exactly what that
            // proves and what it doesn't. `E2E_PAYPAL_MOCK_ENABLED` is a
            // build-time flag, off (dead branch) everywhere but the e2e-gate
            // CI job — see lib/config.ts for the flag definition.
            const { orderId } = await createOrderPromise("paypal");
            await sessionCallbacks.onApprove({ orderId });
          } else {
            const session = sdk.createPayPalOneTimePaymentSession(sessionCallbacks);
            await session.start({ presentationMode: "auto" }, createOrderPromise("paypal"));
          }
        } catch (err: any) {
          if (err?.isRecoverable) {
            console.warn('[PayPal] Recoverable error, user can try again:', err);
          } else {
            console.error('[PayPal] Session error:', err);
            handlePaymentErrorRef.current(err);
          }
        } finally {
          isPaymentInProgressRef.current = false;
        }
      };
      el.addEventListener('click', handleClick);
      return () => el.removeEventListener('click', handleClick);
    },
    // Deliberately minimal deps: `sessionCallbacks` and `createOrderPromise`
    // are re-created every render (unmemoised object / plain function) — if
    // we put them here, the callback ref would swap on every parent render
    // and rip the click handler off the button before the buyer can hit it.
    // Closures capture whatever the FIRST attach saw; that's the same
    // staleness the previous `useEffect(..., [sdk, ...])` shape lived with,
    // so no regression. `sdk` is the only value that must invalidate the
    // handler — a fresh SDK instance means the old session factories no
    // longer work.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sdk],
  );

  const attachPayPalCardButton = useCallback(
    (el: HTMLElement | null) => {
      if (!el || !sdk) return;
      const handleClick = async () => {
        if (isPaymentInProgressRef.current) return;
        isPaymentInProgressRef.current = true;
        setPaymentError(null);
        const session = sdk.createPayPalGuestOneTimePaymentSession(sessionCallbacks);
        try {
          await session.start({ presentationMode: "auto" }, createOrderPromise("card"));
        } catch (err: any) {
          if (err?.isRecoverable) {
            console.warn('[PayPal Card] Recoverable error, user can try again:', err);
          } else {
            console.error('[PayPal Card] Session error:', err);
            handlePaymentErrorRef.current(err);
          }
        } finally {
          isPaymentInProgressRef.current = false;
        }
      };
      el.addEventListener('click', handleClick);
      return () => el.removeEventListener('click', handleClick);
    },
    // Deliberately minimal deps: `sessionCallbacks` and `createOrderPromise`
    // are re-created every render (unmemoised object / plain function) — if
    // we put them here, the callback ref would swap on every parent render
    // and rip the click handler off the button before the buyer can hit it.
    // Closures capture whatever the FIRST attach saw; that's the same
    // staleness the previous `useEffect(..., [sdk, ...])` shape lived with,
    // so no regression. `sdk` is the only value that must invalidate the
    // handler — a fresh SDK instance means the old session factories no
    // longer work.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sdk],
  );

  // -------------------------------------------------------------------------
  // Stripe rail — automatic Session mint (the CTA is gone since 2026-07-29)
  // -------------------------------------------------------------------------
  //
  // The payload the Stripe rail would POST, assembled during render so the
  // effect below has a stable cache key for it. Same `externalShipping`
  // threading the PayPal capture path uses (see `captureAndNotify`) — kept as
  // ONE builder call feeding both the key and the POST, never two copies that
  // could drift.
  //
  // Gated on the same conditions the CTA used to be `disabled` on: no rail, no
  // complete address / rate / intent → no payload → no mint. A guest has no
  // `userId`, so every `getCheckoutPayload` implementation returns null and the
  // rail simply stays idle instead of auto-POSTing into a 401.
  const stripeCheckoutData =
    showStripe && !isButtonDisabled
      ? (getCheckoutPayload?.(
          externalShipping && intent
            ? {
                externalShipping: {
                  sellerId: intent.sellerId,
                  shipmentId: externalShipping.shipmentId,
                  rateId: externalShipping.rateId,
                  amount: externalShipping.amount,
                  carrierProvider: "shippo" as const,
                },
              }
            : undefined,
        ) ?? null)
      : null;

  // Structural — NOT referential — identity of that payload. `getCheckoutPayload`
  // is a fresh closure building a fresh object every parent render, so keying
  // the effect on the object itself would re-POST on every render. Serialising
  // makes the key stable while nothing changed, and different exactly when the
  // money changes (items, quantities, shipping selection, address) — which is
  // precisely when the Session MUST be re-minted: a Session carries frozen
  // amounts, and paying an amount quoted for a superseded cart is the class of
  // bug the CTA used to prevent by construction (mint happened at click time,
  // one instant before payment).
  const stripeMintKey = stripeCheckoutData
    ? JSON.stringify(stripeCheckoutData)
    : null;

  // Latest-ref (react-state-design §10) so the debounced timer POSTs the
  // payload as it stands when it fires, not the closure captured at schedule
  // time. The two only differ within the debounce window, but that window is
  // exactly where the buyer is still typing.
  const stripeCheckoutDataRef = useRef(stripeCheckoutData);
  stripeCheckoutDataRef.current = stripeCheckoutData;

  useEffect(() => {
    if (!stripeMintKey) {
      setStripeRail({ kind: "idle" });
      return;
    }
    // Ignore-flag, not AbortController (react-state-design §10): the builder
    // wraps `fetch` and takes no signal. A superseded mint is dropped on
    // arrival rather than cancelled in flight — the server dedupes by
    // `payload_hash` anyway, so a raced-out response costs nothing.
    let cancelled = false;
    setStripeRail({ kind: "minting" });
    const timer = setTimeout(() => {
      void (async () => {
        const checkoutData = stripeCheckoutDataRef.current;
        if (!checkoutData) return;
        try {
          // `createStripeCheckoutSession` schema-checks the payload against
          // `@kraahl/contract` before fetch — a programmer-bug shape mismatch
          // throws `CheckoutContractError`, caught below and surfaced as the
          // generic error state.
          //
          // PR-L (Sibling-F · F13, 2026-06-27): the Stripe rail consumes the
          // canonical `CheckoutPayload` verbatim — no `StripeCheckoutData`
          // projection.
          const result = await createStripeCheckoutSession({ checkoutData });
          if (cancelled) return;
          if (!result.ok) {
            setStripeRail({
              kind: "error",
              message:
                result.message || result.error || "Stripe checkout failed.",
            });
            return;
          }
          // Embedded Checkout ADT (EMBEDDED_CHECKOUT_PLAN.md §1/§6). Exhaustive
          // switch — a third `kind` fails tsc via `unreachable(result)`.
          switch (result.kind) {
            case "mount":
              setStripeRail({
                kind: "ready",
                clientSecret: result.clientSecret,
                sessionId: result.sessionId,
                sellerStripeAccountId: result.sellerStripeAccountId,
              });
              break;
            case "already_paid":
              // Deliberately NOT an automatic `router.push` any more. Under
              // the CTA this branch answered a click, so yanking the buyer to
              // the return page was a direct response to their action. Minting
              // on mount means this can now fire when someone merely reopens
              // the cart inside the pay→webhook window (draft still
              // unconsumed) — an unrequested redirect. Render an explicit link
              // instead; the navigation itself is unchanged.
              setStripeRail({
                kind: "already_paid",
                sessionId: result.sessionId,
              });
              break;
            default:
              unreachable(result);
          }
        } catch (err) {
          if (cancelled) return;
          // Surface the underlying error — a bare catch makes a network blip,
          // a client-side `CheckoutContractError` and an empty-bodied 4xx look
          // identical in DevTools.
          console.error("[stripe-checkout] session mint threw", err);
          setStripeRail({
            kind: "error",
            message: "Stripe checkout failed to load.",
          });
        }
      })();
    }, STRIPE_MINT_DEBOUNCE_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [stripeMintKey, stripeMintAttempt]);

  // Post-payment teardown. `onSuccess` fires after `checkout.confirm` resolved
  // in place (no Stripe redirect), so this component is still mounted: soft-nav
  // within the `(site)` route group does NOT unmount `GlobalCartServer`. Drop
  // the rail to idle before pushing, or the paid iframe stays mounted over the
  // return page. Idle is safe against a re-mint here — the effect only re-runs
  // when `stripeMintKey` changes, and `closeUtility()` is tearing the cart down.
  const resetStripeRail = useCallback(() => {
    setStripeRail({ kind: "idle" });
  }, []);

  // WS4.3c outer FSM routing. Sub-flow takes over the PayPal area until the
  // buyer confirms address + rate. Mounted BEFORE rail resolution because the
  // sub-flow is provider-agnostic — the buyer needs to pick a rate regardless
  // of which payment rail eventually renders.
  switch (checkoutMode.kind) {
    case "live_rate_collecting":
      if (!parcel || !sellerDetails[0]?.sellerId || !subflowAddress) break;
      return (
        <div className="pt-4 w-full min-w-0">
          <LiveRateSubflow
            sellerId={sellerDetails[0].sellerId}
            parcel={parcel}
            address={subflowAddress}
            onComplete={handleSubflowComplete}
            onCancel={handleSubflowCancel}
            onSelectionChange={handleSubflowSelectionChange}
          />
        </div>
      );
    case "live_rate_ready":
    case "legacy":
      break;
    default:
      unreachable(checkoutMode);
  }

  if (!railResolved && sellerDetails[0]?.sellerId) {
    return (
      <div className="flex w-full justify-center py-10 pt-4">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" aria-hidden />
      </div>
    );
  }

  // Seller is not currently accepting payments from this buyer — either no
  // rail is enabled+connected, or only Stripe is and the buyer is not in the
  // beta. Surface a clear empty state instead of a broken/disabled CTA.
  if (availableProviders.length === 0) {
    return (
      <div className="flex w-full justify-center pt-4">
        <div className="w-full max-w-full rounded-xl border border-amber-200 bg-amber-50 p-4 dark:border-amber-800 dark:bg-amber-950/20">
          <div className="flex items-start gap-3">
            <AlertTriangle
              className="mt-0.5 h-5 w-5 shrink-0 text-amber-600 dark:text-amber-400"
              aria-hidden
            />
            <div className="flex-1 space-y-1">
              <div className="text-sm font-semibold text-amber-800 dark:text-amber-200">
                This seller is not currently accepting payments
              </div>
              <div className="text-sm text-amber-700 dark:text-amber-300">
                You can keep this in your cart and try again later, or message
                the seller to ask when checkout will reopen.
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // No forced-dark scope of its own since Phase 2 step 2f: the host panel
  // (sheet / dialog / cart drawer) follows the site theme, and every theme
  // token below — the rail headings' `text-muted-foreground`, the Stripe Pay
  // button's `bg-primary`, the amber/red banners' `dark:` variants — resolves
  // against whatever scope the panel provides.
  return (
    <div className="pt-4 w-full flex justify-center">
      <div className="w-full max-w-full" style={{ colorScheme: "none" }}>
        {/* Alert: Country banned for shipping */}
        {isCountryBanned && countryToCheck && (
          <div className="mb-4 p-4 bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded-xl">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
              <div className="flex-1">
                <div className="font-semibold text-amber-800 dark:text-amber-200 text-sm">
                  Shipping not available to {getCountryName(countryToCheck)}
                </div>
                <div className="text-amber-700 dark:text-amber-300 text-sm mt-1">
                  Some items in your cart cannot be shipped to this location. Please remove these items or choose a different shipping address to continue.
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Disabled-button reason banner — keeps the buyer from staring at
            dead buttons without knowing why. role=status (not alert) because
            this is descriptive, not an error; assistive tech reads it on the
            next polite turn. */}
        {disabledReason && (
          <div
            role="status"
            aria-live="polite"
            className="mb-4 p-4 bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded-xl"
          >
            <div className="flex items-start gap-3">
              <AlertTriangle
                className="h-5 w-5 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0"
                aria-hidden
              />
              <div className="flex-1">
                <div className="font-semibold text-amber-800 dark:text-amber-200 text-sm">
                  Checkout isn't ready yet
                </div>
                <div className="text-amber-700 dark:text-amber-300 text-sm mt-1">
                  {disabledReason}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Payment error banner — role="alert" container always in DOM for screen reader compatibility */}
        <div role="alert" aria-live="assertive" aria-atomic="true">
          {paymentError && (
            <div className="mb-4 p-4 bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800 rounded-xl">
              <div className="flex items-start gap-3">
                <XCircle className="h-5 w-5 text-red-600 dark:text-red-400 mt-0.5 shrink-0" aria-hidden="true" />
                <div className="flex-1">
                  <div className="font-semibold text-red-800 dark:text-red-200 text-sm">
                    Payment failed
                  </div>
                  <div className="text-red-700 dark:text-red-300 text-sm mt-1">
                    {paymentError.message}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Stripe rail (UX pivot 2026-07-29). The "Pay with Stripe" CTA is
            gone: the Session is minted as soon as the payload is complete and
            the payment form mounts straight away, so the buyer sees the wallet
            buttons + card fields without a click. Both rails now render at the
            same time under their own headings instead of the Stripe card
            replacing everything else — hiding PayPal behind an always-open
            Stripe form would have made it unreachable for sellers who accept
            both. Exhaustive on the FSM; a new `kind` fails tsc below. */}
        {showStripe && (
          <div className="mb-4">
            {bothRailsOffered && <RailHeading>Stripe methods</RailHeading>}

            {stripeRail.kind === "minting" && (
              <div className="flex justify-center py-6">
                <Loader2
                  className="h-6 w-6 animate-spin text-muted-foreground"
                  aria-hidden
                />
                <span className="sr-only">Loading payment methods…</span>
              </div>
            )}

            {stripeRail.kind === "ready" && (
              <StripeEmbeddedCheckoutInline
                open
                clientSecret={stripeRail.clientSecret}
                sellerStripeAccountId={stripeRail.sellerStripeAccountId}
                onSuccess={() => {
                  // SPA navigation, deliberately NOT a Stripe-driven full-page
                  // redirect: a full-page landing remounts every root-layout
                  // provider and re-runs the parallel refresh-token race that
                  // logged buyers out right after paying.
                  // @see .tondocs/infra/AUTH_REFRESH_RACE.md
                  //
                  // Teardown before push: `/checkout/stripe-return` sits in the
                  // same `(site)` route group as `GlobalCartServer`, so soft-nav
                  // does NOT unmount the cart sheet or this form. Without the
                  // reset, the Pay button spins forever over the return page
                  // (the iframe keeps `submitting=true` expecting an unmount
                  // that never comes).
                  const { sessionId } = stripeRail;
                  resetStripeRail();
                  closeUtility();
                  router.push(
                    `/checkout/stripe-return?session_id=${sessionId}`,
                  );
                }}
                onLoadError={(err) => {
                  console.error(
                    "[stripe-checkout] embedded checkout failed to load",
                    err,
                  );
                  setStripeRail({
                    kind: "error",
                    message: "Stripe checkout failed to load.",
                  });
                }}
              />
            )}

            {stripeRail.kind === "already_paid" && (
              <div className="rounded-xl border border-border bg-muted/40 p-4">
                <div className="text-sm font-semibold">
                  This checkout was already paid
                </div>
                <div className="mt-1 text-sm text-muted-foreground">
                  We are still confirming it. Open the status page to see where
                  your order stands.
                </div>
                <Button
                  type="button"
                  variant="outline"
                  className="mt-3"
                  onClick={() => {
                    const { sessionId } = stripeRail;
                    resetStripeRail();
                    closeUtility();
                    router.push(
                      `/checkout/stripe-return?session_id=${sessionId}`,
                    );
                  }}
                >
                  View order status
                </Button>
              </div>
            )}

            {stripeRail.kind === "error" && (
              <div className="rounded-xl border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950/20 p-4">
                <div className="flex items-start gap-3">
                  <XCircle
                    className="h-5 w-5 text-red-600 dark:text-red-400 mt-0.5 shrink-0"
                    aria-hidden
                  />
                  <div className="flex-1">
                    <div className="text-sm font-semibold text-red-800 dark:text-red-200">
                      Card payment is unavailable right now
                    </div>
                    <div className="mt-1 text-sm text-red-700 dark:text-red-300">
                      {stripeRail.message}
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="mt-3"
                      onClick={() => setStripeMintAttempt((n) => n + 1)}
                    >
                      Try again
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* PayPal block — only when seller accepts PayPal AND it is one of
            the rails available to this buyer. Wraps the entire v6 SDK render
            (PayPal button + card + Apple Pay + Google Pay) plus the
            sandbox demo panel. */}
        {showPayPal && (
          <>
            {bothRailsOffered && <RailHeading>PayPal methods</RailHeading>}
                {/* Demo panel — sandbox negative testing */}
                {showDemoPanel && (
                  <div className="mb-3 p-3 bg-yellow-50 dark:bg-yellow-950/20 border border-yellow-300 dark:border-yellow-700 rounded-lg">
                    <div className="text-xs font-semibold text-yellow-800 dark:text-yellow-200 mb-1.5">
                      Sandbox Testing
                    </div>
                    <select
                      value={demoMockCode}
                      onChange={(e) => setDemoMockCode(e.target.value)}
                      className="w-full text-base px-2 py-1.5 rounded border border-yellow-300 dark:border-yellow-600 bg-white dark:bg-yellow-950 text-yellow-900 dark:text-yellow-100"
                    >
                      <option value="">Normal (no mock)</option>
                      <option value="INSTRUMENT_DECLINED">INSTRUMENT_DECLINED</option>
                      <option value="TRANSACTION_REFUSED">TRANSACTION_REFUSED</option>
                      <option value="INSUFFICIENT_FUNDS">INSUFFICIENT_FUNDS</option>
                      <option value="INTERNAL_SERVER_ERROR">INTERNAL_SERVER_ERROR</option>
                      <option value="PAYER_CANNOT_PAY">PAYER_CANNOT_PAY</option>
                      <option value="CARD_EXPIRED">CARD_EXPIRED</option>
                    </select>
                  </div>
                )}

                {/* SDK loading state */}
                {isLoading && !isButtonDisabled && (
                  <div className="flex justify-center py-4">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                  </div>
                )}

                {/* SDK error state */}
                {sdkError && (
                  <div className="text-sm text-red-600 dark:text-red-400 text-center py-2">
                    Failed to load payment options. Please refresh the page.
                  </div>
                )}

                {/* PayPal button (web component) */}
                {/*[&>*]:block [&>*]:w-full forces all direct children to display: block;
                      width: 100% — this overrides whatever default sizing the web component has.*/}
                {!isLoading && isPayPalEligible && (
                  <div
                    className="[&>*]:block [&>*]:w-full [&>*]:h-[55px]"
                    style={{
                      ...(isButtonDisabled ? { pointerEvents: "none", opacity: 0.5 } : {}),
                      "--paypal-button-border-radius": "30px",
                    } as React.CSSProperties}
                  >
                    {/* @ts-expect-error — paypal-button is a v6 web component, typed in types/paypal-v6.d.ts */}
                    <paypal-button ref={attachPayPalButton} type="pay" class="paypal-gold" />
                  </div>
                )}

                {/* Card button — guest checkout without PayPal account.
                    Container must be allowed to grow: after click, PayPal expands the card form
                    inline inside <paypal-basic-card-container>. Fixed height on descendants
                    would trap the form and cause later siblings (Google Pay) to overlap it. */}
                {!isLoading && isPayPalEligible && (
                  <div
                    className="mt-2"
                    style={{
                      ...(isButtonDisabled ? { pointerEvents: "none", opacity: 0.5 } : {}),
                      "--paypal-button-border-radius": "30px",
                    } as React.CSSProperties}
                  >
                    {/* @ts-expect-error — paypal-basic-card-* are v6 web components */}
                    <paypal-basic-card-container style={{ display: "block", width: "100%" }}>
                      {/* @ts-expect-error — v6 web component */}
                      <paypal-basic-card-button ref={attachPayPalCardButton} style={{ display: "block", width: "100%", height: "55px" }} />
                    {/* @ts-expect-error — v6 web component */}
                    </paypal-basic-card-container>
                  </div>
                )}

                {/* Apple Pay button — single-seller only (PayPal rejects multiple purchase_units for digital wallets) */}
                {!isLoading && APPLE_PAY_ENABLED && isApplePayEligible && allSellersHaveApplePay && sdk && sellerDetails.length === 1 && (
                  <ApplePayButton
                    sdk={sdk}
                    totalAmount={totalAmountStr}
                    currencyCode={currencyCode}
                    disabled={isButtonDisabled}
                    // Apple Pay is a card-network wallet → pass fundingSource:'card'
                    // so the server stamps the seller into soft_descriptor
                    // (KRAAHL <seller>), not bare KRAAHL. Bare descriptor is the
                    // 2026-05-29 UNAUTHORISED-chargeback class. See finding #1.
                    createOrder={() => createOrderFromIntent(undefined, "card")}
                    captureAndNotify={(orderId) => captureAndNotifyRef.current(orderId)}
                    onError={(err) => handlePaymentErrorRef.current(err as PayPalV6Error)}
                    paymentInProgressRef={isPaymentInProgressRef}
                  />
                )}

                {/* Google Pay button — single-seller only (PayPal rejects multiple purchase_units for Google Pay) */}
                {!isLoading && GOOGLE_PAY_ENABLED && isGooglePayEligible && allSellersHaveGooglePay && sdk && sellerDetails.length === 1 && (
                  <GooglePayButton
                    sdk={sdk}
                    totalAmount={totalAmountStr}
                    currencyCode={currencyCode}
                    disabled={isButtonDisabled}
                    createOrder={() => createOrderFromIntent({
                      google_pay: {
                        attributes: {
                          verification: { method: "SCA_WHEN_REQUIRED" }
                        }
                      }
                    }, "card")}
                    captureAndNotify={(orderId) => captureAndNotifyRef.current(orderId)}
                    onError={(err) => handlePaymentErrorRef.current(err as PayPalV6Error)}
                    paymentInProgressRef={isPaymentInProgressRef}
                  />
                )}
          </>
        )}
      </div>
    </div>
  );
}
